import {useEffect, useRef, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {Table, Tag, Alert, Tabs, Badge, Input, Segmented} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon, Loading, PageHeader, ProjectToolNav} from "~/components";
import {
	useGetCrawlStatusQuery,
	useGetCrawlReportQuery,
	useGetIssueMarksQuery,
	useSetIssueMarkMutation,
} from "~/reduxs/api/crawlerApiSlice";
import {markKey} from "../components/IssueMark";
import ReportSummary from "../components/ReportSummary";
import SiteScoreCards from "../components/SiteScoreCards";
import CrawlProgress from "../components/CrawlProgress";
import IssueSection from "../components/IssueSection";
import PageIssueList from "../components/PageIssueList";
import style from "../style/SiteCrawler.module.scss";

const cn = className.bind(style);

const GRADE_COLOR = {A: 'green', B: 'lime', C: 'gold', D: 'orange', F: 'red'};

/** Tách issue theo trang (type `page_*`) khỏi issue cấp site (Tầng B). */
const isPageIssue = (i) => typeof i.type === 'string' && i.type.startsWith('page_');

function splitIssues(issues) {
	const site = {error: [], warning: [], info: []};
	const page = [];
	['error', 'warning', 'info'].forEach((sev) => {
		(issues?.[sev] || []).forEach((i) => (isPageIssue(i) ? page.push(i) : site[sev].push(i)));
	});
	return {site, page};
}

const statusCodeColor = (code) => {
	if (code >= 200 && code < 300) return 'green';
	if (code >= 300 && code < 400) return 'gold';
	if (code >= 400 && code < 500) return 'orange';
	if (code >= 500) return 'red';
	return 'default';
};

/**
 * Dựng dữ liệu xuất Excel/PDF cho báo cáo crawl. Excel đầy đủ mọi trang/vấn đề;
 * PDF cắt bớt (trang điểm thấp nhất + vấn đề đầu tiên) để file không phình quá lớn.
 */
function buildCrawlExport(report, split, crawlId) {
	const crawl = report.crawl || {};

	const overviewRows = [
		['Điểm SEO trung bình', Number(crawl.avg_score || 0)],
		['Số trang đã phân tích', crawl.pages_crawled],
		['Lỗi nặng', report.issue_counts?.error || 0],
		['Cảnh báo', report.issue_counts?.warning || 0],
		['Thông tin', report.issue_counts?.info || 0],
	];

	const GROUP_LABELS = [
		['score_technical', 'Kỹ thuật & Index'], ['score_meta', 'Thẻ Meta'], ['score_social', 'Mạng xã hội'],
		['score_content', 'Nội dung'], ['score_image', 'Hình ảnh'], ['score_structured', 'Cấu trúc & Link'],
	];
	const groupRows = GROUP_LABELS.map(([key, label]) => [label, Number(crawl[key] || 0)]);

	const SEV_LABEL = {error: 'Lỗi nặng', warning: 'Cảnh báo', info: 'Thông tin'};
	const siteIssueRows = ['error', 'warning', 'info'].flatMap((sev) =>
		split.site[sev].map((i) => [SEV_LABEL[sev], i.message, i.url || '']));
	const pageIssueRows = split.page.map((i) => [i.url || `Trang #${i.page_id}`, i.message]);

	const pageHead = ['URL', 'HTTP', 'Độ sâu', 'Điểm', 'Hạng', 'H1', 'Số từ', 'Lỗi'];
	const pageRows = [...report.pages]
		.sort((a, b) => a.score - b.score)
		.map((p) => [p.url, p.status_code, p.depth, p.score, p.grade, p.h1_count, p.word_count, p.issues]);

	const buildExcel = () => [
		{name: 'Tổng quan', aoa: [['Chỉ số', 'Giá trị'], ...overviewRows, [], ['Nhóm tiêu chí', 'Điểm'], ...groupRows]},
		{name: 'Vấn đề cấp site', aoa: [['Mức độ', 'Vấn đề', 'URL'], ...siteIssueRows]},
		{name: 'Vấn đề theo trang', aoa: [['URL', 'Vấn đề'], ...pageIssueRows]},
		{name: 'Các trang', aoa: [pageHead, ...pageRows]},
	];

	const buildPdf = () => ({
		title: 'Báo cáo Site Crawler',
		subtitle: `Lần crawl #${crawlId}`,
		sections: [
			{heading: 'Tổng quan', columns: ['Chỉ số', 'Giá trị'], rows: overviewRows},
			{heading: 'Điểm theo nhóm', columns: ['Nhóm tiêu chí', 'Điểm'], rows: groupRows},
			{heading: `Vấn đề cấp site (${Math.min(siteIssueRows.length, 200)}/${siteIssueRows.length})`,
				columns: ['Mức độ', 'Vấn đề', 'URL'], rows: siteIssueRows.slice(0, 200)},
			{heading: `Trang điểm thấp nhất (${Math.min(pageRows.length, 100)}/${pageRows.length})`,
				columns: pageHead, rows: pageRows.slice(0, 100)},
		],
	});

	return {filename: `bao-cao-crawl-${crawlId}`, buildExcel, buildPdf};
}

/**
 * Báo cáo 1 lần crawl. Đang running → thanh tiến độ (poll). Xong → hero tổng quan +
 * Tabs (Tổng quan điểm 6 nhóm / Vấn đề cấp site + theo trang / Bảng các trang).
 */
function CrawlReport() {

	const {id} = useParams();
	const navigate = useNavigate();

	// Poll khi đang chạy; dừng poll khi đã done/failed.
	const [poll, setPoll] = useState(5000);
	const [pageKeyword, setPageKeyword] = useState('');
	const [pageFilter, setPageFilter] = useState('all'); // all | issues
	const {data: status, isLoading: loadingStatus, fulfilledTimeStamp} = useGetCrawlStatusQuery(id, {
		pollingInterval: poll,
		skipPollingIfUnfocused: true,
	});

	const isRunning = status?.status === 'running';
	const isDone = status?.status === 'done';
	const isFailed = status?.status === 'failed';

	useEffect(() => {
		setPoll(status && status.status !== 'running' ? 0 : 5000);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status?.status]);

	// Phát hiện crawl "treo": đang running nhưng số URL đã xử lý không nhích qua nhiều
	// lần poll (nghi cronjob schedule-run không chạy — crawl sẽ đứng ở running vô hạn).
	const STALL_AFTER_MS = 3 * 60 * 1000;
	const stallRef = useRef({value: -1, since: 0});
	const [stalled, setStalled] = useState(false);

	useEffect(() => {
		if (!isRunning) {
			stallRef.current = {value: -1, since: 0};
			setStalled(false);
			return;
		}
		const processed = status?.progress?.processed ?? 0;
		const now = Date.now();
		if (processed !== stallRef.current.value || !stallRef.current.since) {
			stallRef.current = {value: processed, since: now};
			setStalled(false);
		} else if (now - stallRef.current.since >= STALL_AFTER_MS) {
			setStalled(true);
		}
		// fulfilledTimeStamp đổi theo TỪNG lần poll (kể cả khi data không đổi) — dep chuẩn để tick.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRunning, fulfilledTimeStamp]);

	const {data: report, isLoading: loadingReport} = useGetCrawlReportQuery(id, {skip: !isDone});

	// Đánh dấu xử lý issue — khoá (type, ref) theo PROJECT nên giữ qua các lần crawl.
	const {data: markRows = []} = useGetIssueMarksQuery(status?.project_id, {skip: !status?.project_id || !isDone});
	const [setIssueMark] = useSetIssueMarkMutation();

	if (loadingStatus) return <Loading />;
	if (!status) return <div className="container">Không tìm thấy lần crawl.</div>;

	const projectId = status.project_id;
	const openPage = (pageId) => navigate(`/crawler/pages/${pageId}`);

	const marks = new Map(markRows.map((m) => [markKey(m.type, m.ref), m.status]));
	const handleMark = (type, ref, statusValue) => setIssueMark({projectId, type, ref, status: statusValue});

	const split = report ? splitIssues(report.issues) : null;
	const siteCount = split
		? split.site.error.length + split.site.warning.length + split.site.info.length
		: 0;

	// Lọc bảng trang: từ khoá (URL/tiêu đề) + chỉ trang có lỗi.
	const kw = pageKeyword.trim().toLowerCase();
	const pagesWithIssues = report ? report.pages.filter((p) => p.issues > 0).length : 0;
	const filteredPages = report
		? report.pages.filter((p) => {
			if (pageFilter === 'issues' && !(p.issues > 0)) return false;
			if (kw && !((p.url || '').toLowerCase().includes(kw) || (p.title || '').toLowerCase().includes(kw))) return false;
			return true;
		})
		: [];

	const columns = [
		{
			title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true,
			render: (url, r) => (
				<span className={cn('urlCell')} onClick={() => openPage(r.id)}>{url}</span>
			),
		},
		{
			title: 'HTTP', dataIndex: 'status_code', key: 'status_code', width: 84,
			render: (c) => <Tag color={statusCodeColor(c)}>{c || '—'}</Tag>,
		},
		// Cột phụ — ẩn dưới 768px
		{title: 'Depth', dataIndex: 'depth', key: 'depth', width: 74, align: 'center', responsive: ['md']},
		{
			title: 'Điểm', dataIndex: 'score', key: 'score', width: 100,
			sorter: (a, b) => a.score - b.score,
			defaultSortOrder: 'ascend',
			render: (s, r) => (r.status_code >= 200 && r.status_code < 300
				? <span><strong>{s}</strong> <Tag color={GRADE_COLOR[r.grade]}>{r.grade}</Tag></span>
				: '—'),
		},
		{title: 'H1', dataIndex: 'h1_count', key: 'h1', width: 60, align: 'center', responsive: ['md']},
		{title: 'Từ', dataIndex: 'word_count', key: 'words', width: 80, align: 'center', responsive: ['md']},
		{
			title: 'Lỗi', dataIndex: 'issues', key: 'issues', width: 72, align: 'center',
			render: (n) => (n > 0 ? <Tag color="orange">{n}</Tag> : <Tag color="green">0</Tag>),
		},
		{
			title: '', key: 'actions', width: 56,
			render: (_, r) => (
				<Button small blue onClick={() => openPage(r.id)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-eye" />} />
			),
		},
	];

	const tabItems = report ? [
		{
			key: 'overview',
			label: <span><FontAwesomeIcon icon="fa-light fa-gauge-high" /> Điểm theo nhóm</span>,
			children: (
				<div className={cn('card')}>
					<SiteScoreCards crawl={report.crawl} />
				</div>
			),
		},
		{
			key: 'issues',
			label: (
				<span>
					<FontAwesomeIcon icon="fa-light fa-triangle-exclamation" /> Vấn đề{' '}
					<Badge count={siteCount + split.page.length} color="#fa8c16" overflowCount={999} />
				</span>
			),
			children: (
				<>
					<div className={cn('card')}>
						<div className={cn('blockHead')}>
							<h4 className={cn('blockTitle')}>
								<FontAwesomeIcon icon="fa-light fa-sitemap" /> Vấn đề cấp site
								<span className={cn('countMuted')}>({siteCount})</span>
							</h4>
							<p className={cn('blockDesc')}>
								Lỗi chỉ phát hiện được khi nhìn toàn site: trùng lặp, link hỏng, trang mồ côi, chuyển hướng, lỗi server.
							</p>
						</div>
						<IssueSection issues={split.site} onOpenPage={openPage} marks={marks} onMark={handleMark} />
					</div>

					<div className={cn('card')} style={{marginTop: 16}}>
						<div className={cn('blockHead')}>
							<h4 className={cn('blockTitle')}>
								<FontAwesomeIcon icon="fa-light fa-file-circle-exclamation" /> Vấn đề theo trang
								<span className={cn('countMuted')}>({split.page.length})</span>
							</h4>
							<p className={cn('blockDesc')}>
								Tiêu chí SEO on-page chưa đạt trên từng trang — gom theo loại, bấm URL để xem chi tiết.
							</p>
						</div>
						<PageIssueList issues={split.page} onOpenPage={openPage} marks={marks} onMark={handleMark} />
					</div>
				</>
			),
		},
		{
			key: 'pages',
			label: (
				<span>
					<FontAwesomeIcon icon="fa-light fa-table-list" /> Các trang{' '}
					<Badge count={report.pages.length} color="#4f46e5" overflowCount={9999} />
				</span>
			),
			children: (
				<div className={cn('card')}>
					<div className={cn('pagesToolbar')}>
						<p className={cn('blockDesc')} style={{margin: 0}}>
							Xếp hạng theo điểm — trang điểm thấp / nhiều lỗi lên trước để ưu tiên sửa.
						</p>
						<div className={cn('pagesControls')}>
							<Segmented
								value={pageFilter}
								onChange={setPageFilter}
								options={[
									{label: `Tất cả (${report.pages.length})`, value: 'all'},
									{label: `Có lỗi (${pagesWithIssues})`, value: 'issues'},
								]}
							/>
							<Input
								allowClear
								prefix={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}
								placeholder="Tìm theo URL hoặc tiêu đề…"
								value={pageKeyword}
								onChange={(e) => setPageKeyword(e.target.value)}
								className={cn('pagesSearch')}
							/>
						</div>
					</div>
					<Table
						rowKey="id"
						columns={columns}
						dataSource={filteredPages}
						size="middle"
						scroll={{x: 'max-content'}}
						locale={{emptyText: kw
							? `Không có trang nào khớp “${pageKeyword}”`
							: (pageFilter === 'issues' ? 'Không có trang nào bị lỗi 🎉' : 'Không có trang')}}
						pagination={{pageSize: 20, showSizeChanger: true, hideOnSinglePage: true}}
					/>
				</div>
			),
		},
	] : [];

	return (
		<div className="container">
			<ProjectToolNav projectId={projectId} active="crawler"
				trail={[{label: `Crawl #${status.id}`}]} />

			<PageHeader
				icon="fa-light fa-chart-simple"
				title="Báo cáo crawl"
				subtitle={`Lần crawl #${status.id}`}
			/>

			{isFailed && (
				<Alert type="error" showIcon
					message="Crawl thất bại"
					description={status.error_message || 'Đã xảy ra lỗi trong quá trình crawl.'}
				/>
			)}

			{isRunning && stalled && (
				<Alert type="warning" showIcon style={{marginBottom: 16}}
					message="Crawl không có tiến triển trong hơn 3 phút"
					description="Crawl chạy nền theo cronjob (schedule-run). Nếu số liệu tiếp tục đứng yên, hãy kiểm tra cronjob trên server có đang chạy không — không có cron, crawl sẽ đứng ở trạng thái 'Đang chạy' vô hạn." />
			)}

			{isRunning && <CrawlProgress progress={status.progress} />}

			{isDone && (loadingReport || !report ? <Loading noFixed /> : (
				<>
					<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
						<ExportButtons {...buildCrawlExport(report, split, status.id)} />
					</div>
					<ReportSummary
						crawl={report.crawl}
						issueCounts={report.issue_counts}
						statusDist={report.status_dist}
					/>
					<div className={cn('reportTabs')}>
						<Tabs items={tabItems} />
					</div>
				</>
			))}
		</div>
	);
}

export default CrawlReport;
